import React from 'react';
import get from 'lodash/fp/get';
import ContributionType from '@/constants/ContributionType';
import EntityContentType from '@/constants/EntityContentType';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { MarkdownItems } from '@/modules/Feed';
import type Campaign from '@/types/api/Campaign';
import type Constituent from '@/types/api/Constituent';
import AnnouncementSummary from './AnnouncementSummary';
import CampaignSummary from './CampaignSummary';
import DonatedViaSummary from './DonatedViaSummary';
import DonationForSummary from './DonationForSummary';
import DonationSummary from './DonationSummary';
import EmailIntegrationReceivedSummary from './EmailIntegrationReceivedSummary';
import EmailIntegrationSentSummary from './EmailIntegrationSentSummary';
import GrantDonationSummary from './GrantDonationSummary';
import InteractionSummary from './InteractionSummary';
import SponsorshipContributionSummary from './SponsorshipContributionSummary';
import TicketContributionSummary from './TicketContributionSummary';
import TimeDonationSummary from './TimeDonationSummary';

const getRestriction = get('restrictions[0].restrictionDescription');
// @ts-expect-error TS(7031) TODO(ts-migrate): Binding element 'inKind' implicitly has an 'any' t... Remove this comment to see the full error message
const getInKind = ({ inKind: [inKind] }) =>
  `${inKind.inKindItemQuantity} ${inKind.inKindItemDescription}`;

type EntityDetailsSummaryContentProps = {
  constituent?: Constituent;
  entity: object;
  entityType: string;
  markdownContent?: {
    txt?: string;
  }[];
};

export default function EntityDetailsSummaryContent({
  constituent,
  entity,
  entityType,
  markdownContent,
}: EntityDetailsSummaryContentProps) {
  const content = React.useMemo(() => {
    // If markdownContent is truthy, then we are using the markdown coming
    // from the back-end to render the UI. In that case, do nothing.
    if (markdownContent) {
      return null;
    }

    if (entityType === EntityContentType.DONATION) {
      // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'donationTypeEnum' does not exist on type... Remove this comment to see the full error message
      const { donationTypeEnum } = entity;

      switch (donationTypeEnum) {
        case ContributionType.GENERAL:
        case ContributionType.PLEDGE:
          return <DonatedViaSummary entity={entity} />;
        case ContributionType.GRANT:
          return <GrantDonationSummary entity={entity} />;
        case ContributionType.IN_KIND:
          return <DonationForSummary entity={entity} subjectGet={getInKind} />;
        case ContributionType.PLANNED_GIVING:
        case ContributionType.RECURRING:
          return <DonationSummary entity={entity} />;
        case ContributionType.RESTRICTED:
          return (
            <DonationForSummary entity={entity} subjectGet={getRestriction} />
          );
        case ContributionType.TIME:
          return <TimeDonationSummary entity={entity} />;
        case ContributionType.SPONSORSHIP:
          return <SponsorshipContributionSummary entity={entity} />;
        case ContributionType.TICKET:
          return <TicketContributionSummary entity={entity} />;

        default:
          return null;
      }
    } else if (entityType === EntityContentType.INTERACTION && constituent) {
      return <InteractionSummary constituent={constituent} entity={entity} />;
    } else if (
      entityType === EntityContentType.CAMPAIGN_CREATED ||
      entityType === EntityContentType.CAMPAIGN_GOAL_MET ||
      entityType === EntityContentType.CAMPAIGN_STRETCH_GOAL_ADDED ||
      entityType === EntityContentType.CAMPAIGN_STRETCH_GOAL_MET
    ) {
      return (
        <CampaignSummary entity={entity as Campaign} entityType={entityType} />
      );
    } else if (entityType === EntityContentType.EMAIL_RECEIVED) {
      return <EmailIntegrationReceivedSummary entity={entity} />;
    } else if (entityType === EntityContentType.EMAIL_SENT) {
      return <EmailIntegrationSentSummary entity={entity} />;
    } else if (entityType === EntityContentType.ANNOUNCEMENT) {
      return <AnnouncementSummary entity={entity} />;
    }

    return null;
  }, [entity, entityType, markdownContent]);

  return markdownContent ? (
    // @ts-expect-error TS(2786) TODO(ts-migrate): 'MarkdownItems' cannot be used as a JSX component.
    <MarkdownItems body={markdownContent} />
  ) : (
    <div className="text-base font-normal">{content}</div>
  );
}
